import {Color} from '@benchling/nucleus-ui';
import {Grid} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  CH3,
  FC,
  getSingleDomainModule,
  getTwoDomainModule,
  HETERO_CH3,
  HETERO_FC,
  HETERO_IGG,
  HINGE,
  IGG,
  MONO_CH3,
  MONO_FC,
  SCFV,
  SCFAB,
  FAB,
  FUSION,
} from 'antibodies/format/help-modal/constants';
import {sharedStyles} from 'antibodies/format/help-modal/shared';
import Antibody from 'antibodies/visualization/Antibody';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_PROXIMA_12, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';

interface ModuleVisualProps {
  label: string;
  format: AntibodyFormat;
  showLabels?: boolean;
}

const MODULE_VISUAL_SIZE = 100;

export function ModuleVisual({label, format, showLabels = false}: ModuleVisualProps): JSX.Element {
  return (
    <div className={css(styles.moduleVisualBox)}>
      <div className={css(styles.moduleAntibodyContainer)}>
        <Antibody format={format} showLabels={showLabels} />
      </div>
      <span className={css(styles.moduleVisualLabel)}>{label}</span>
    </div>
  );
}

interface DescriptionItemProps {
  name: string;
  description: string;
}

export function DescriptionItem({name, description}: DescriptionItemProps): JSX.Element {
  return (
    <div className={css(styles.descriptionItem)}>
      <span className={css(styles.descriptionName)}>{name}:</span>
      <span>{description}</span>
    </div>
  );
}

export function ComponentsTab(): JSX.Element {
  return (
    <div>
      <section className={css(sharedStyles.section)}>
        <h4>Basic modules</h4>
        <div className={css(styles.visualizationsRow)}>
          <ModuleVisual
            label="VHH"
            format={getSingleDomainModule(AntibodyDomainType.VHH)}
            showLabels={true}
          />
          <ModuleVisual label="VH" format={getSingleDomainModule(AntibodyDomainType.VH)} showLabels={true} />
          <ModuleVisual label="VL" format={getSingleDomainModule(AntibodyDomainType.VL)} showLabels={true} />
          <ModuleVisual
            label="CH1"
            format={getSingleDomainModule(AntibodyDomainType.CH1)}
            showLabels={true}
          />
          <ModuleVisual
            label="CH2"
            format={getSingleDomainModule(AntibodyDomainType.CH2)}
            showLabels={true}
          />
          <ModuleVisual
            label="CH3"
            format={getSingleDomainModule(AntibodyDomainType.CH3)}
            showLabels={true}
          />
          <ModuleVisual label="CL" format={getSingleDomainModule(AntibodyDomainType.CL)} showLabels={true} />
        </div>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <DescriptionItem name="VHH/dAb" description="Variable heavy single domain" />
            <DescriptionItem name="VH" description="Variable heavy domain" />
            <DescriptionItem name="VL" description="Variable light domain" />
          </Grid.Col>
          <Grid.Col span={6}>
            <DescriptionItem name="CH1, CH2, CH3" description="Constant heavy domains" />
            <DescriptionItem name="CL" description="Constant light domain" />
          </Grid.Col>
        </Grid>
      </section>

      <section className={css(sharedStyles.section)}>
        <h4>Complex modules</h4>
        <div className={css(styles.visualizationsRow)}>
          <ModuleVisual
            label="Fd"
            format={getTwoDomainModule(AntibodyDomainType.VH, AntibodyDomainType.CH1)}
            showLabels={true}
          />
          <ModuleVisual
            label="LC"
            format={getTwoDomainModule(AntibodyDomainType.VL, AntibodyDomainType.CL)}
            showLabels={true}
          />
          <ModuleVisual label="Fab" format={FAB} />
          <ModuleVisual label="scFv" format={SCFV} />
          <ModuleVisual label="scFab" format={SCFAB} />
          <ModuleVisual label="F" format={FUSION} />
        </div>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <DescriptionItem name="Fd" description="VH-CH1" />
            <DescriptionItem name="LC" description="VL-CL" />
            <DescriptionItem name="Fab" description="Fragment antigen-binding" />
          </Grid.Col>
          <Grid.Col span={6}>
            <DescriptionItem name="scFv" description="Single-chain variable fragment" />
            <DescriptionItem name="scFab" description="Single-chain Fab" />
            <DescriptionItem name="F" description="Fusion domain" />
          </Grid.Col>
        </Grid>
      </section>

      <section className={css(sharedStyles.section)}>
        <h4>Dimerization centers</h4>
        <div className={css(styles.visualizationsRow)}>
          <ModuleVisual label="IgG" format={IGG} />
          <ModuleVisual label="heteroIgG" format={HETERO_IGG} />
          <ModuleVisual label="Fc" format={FC} />
          <ModuleVisual label="heteroFc" format={HETERO_FC} />
          <ModuleVisual label="Fab" format={FAB} />
          <ModuleVisual label="CH3" format={CH3} />
          <ModuleVisual label="heteroCH3" format={HETERO_CH3} />
          <ModuleVisual label="hinge" format={HINGE} />
        </div>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <DescriptionItem name="IgG" description="Full immunoglobulin" />
            <DescriptionItem name="heteroIgG" description="Asymmetric IgG with different chains" />
            <DescriptionItem name="Fc" description="Symmetric Fragment crystallization region" />
            <DescriptionItem name="heteroFc" description="Asymmetric Fc" />
          </Grid.Col>
          <Grid.Col span={6}>
            <DescriptionItem name="Fab" description="Fragment antigen-binding" />
            <DescriptionItem name="CH3" description="Symmetric CH3 with hinge" />
            <DescriptionItem name="heteroCH3" description="Asymmetric CH3 with hinge" />
            <DescriptionItem name="hinge" description="Hinge only center" />
          </Grid.Col>
        </Grid>
      </section>

      <section className={css(sharedStyles.section)}>
        <h4>Monomerization centers</h4>
        <div className={css(styles.visualizationsRow)}>
          <ModuleVisual label="monoFc" format={MONO_FC} />
          <ModuleVisual label="monoCH3" format={MONO_CH3} />
          <ModuleVisual label="scFv" format={SCFV} />
          <ModuleVisual label="scFab" format={SCFAB} />
          <ModuleVisual label="VHH" format={getSingleDomainModule(AntibodyDomainType.VHH)} />
        </div>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <DescriptionItem name="monoFc" description="Single Fc" />
            <DescriptionItem name="monoCH3" description="Single CH3" />
            <DescriptionItem name="scFv" description="Single-chain variable fragment" />
          </Grid.Col>
          <Grid.Col span={6}>
            <DescriptionItem name="scFab" description="Single-chain Fab" />
            <DescriptionItem name="VHH" description="Variable heavy single domain" />
          </Grid.Col>
        </Grid>
      </section>
    </div>
  );
}

const styles = StyleSheet.create({
  moduleVisualBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  moduleAntibodyContainer: {
    width: MODULE_VISUAL_SIZE,
    height: MODULE_VISUAL_SIZE,
    backgroundColor: Color.GRAY_200,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  moduleVisualLabel: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_600,
  },
  descriptionItem: {
    display: 'flex',
    gap: '4px',
  },
  descriptionName: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  visualizationsRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    padding: '16px',
    backgroundColor: Color.GRAY_50,
    borderRadius: '8px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
});
